import { Injectable, TemplateRef, ViewContainerRef } from '@angular/core';
import { ComponentType, ComponentPortal, TemplatePortal } from '@angular/cdk/portal';
import { Overlay, OverlayRef, OverlayPositionBuilder, ScrollStrategy } from '@angular/cdk/overlay';

export interface DynamicTooltipOverlayOptions {
  /** Use reposition scroll strategy (e.g. scrollable Scraii fullscreen gantt). Default: close on scroll. */
  scrollReposition?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class DynamicCustomTooltipService {

  private overlayRef: OverlayRef | null = null;

  constructor(private overlay: Overlay) {}

  showTooltip(
    tooltipContent: TemplateRef<any> | ComponentType<any>,
    viewContainerRef: ViewContainerRef,
    event:any,
    tooltip?:any,
    options?: DynamicTooltipOverlayOptions
  ) {
    // Hide any existing tooltip
    this.hideTooltip();
    // Determine the position strategy
    const positionStrategy:any = this.overlay.position()
      .flexibleConnectedTo(event)
      .withFlexibleDimensions(true)
      .withPush(true)
      .withViewportMargin(10)

    if(!tooltip) {
      positionStrategy
      .withPositions([
        {
          originX: 'start', overlayX: 'start',
          originY: 'bottom', overlayY: 'top',
        },
        {
          originX: 'start', overlayX: 'start',
          originY: 'top', overlayY: 'bottom',
        },
        {
          originX: 'end', overlayX: 'start',
          originY: 'center', overlayY: 'center',
        },
        {
          originX: 'start', overlayX: 'end',
          originY: 'center', overlayY: 'center',
        },
      ])
    } else {
      positionStrategy
      .withPositions([
        {
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom',
          offsetX: 0,
          offsetY: -8,
        }
      ]);
    }

    const scrollStrategy: ScrollStrategy = options?.scrollReposition
      ? this.overlay.scrollStrategies.reposition()
      : this.overlay.scrollStrategies.close();
    
    // Create overlay
    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy,
      panelClass: 'custom-tooltip',
      hasBackdrop: !tooltip ? true : false,
      backdropClass: 'cdk-overlay-transparent-backdrop'
    });
  
    // Attach tooltip content
    let containerPortal: TemplatePortal<any> | ComponentPortal<any>;
    if (tooltipContent instanceof TemplateRef) {
      containerPortal = new TemplatePortal(tooltipContent, viewContainerRef);
    } else {
      containerPortal = new ComponentPortal(tooltipContent, viewContainerRef);
    }
  
    this.overlayRef.attach(containerPortal);

    // Close tooltip on clicking outside
    this.overlayRef.backdropClick().subscribe(() => {
      this.hideTooltip();
    })
  }

  // Hide Tooltip
  hideTooltip() {
    if (this.overlayRef) {
      this.overlayRef.detach();
    }
  }

  createMouseTooltip(
    tooltipContent: TemplateRef<any> | ComponentType<any>,
    viewContainerRef: ViewContainerRef,
    event: MouseEvent
  ) {
    if (this.overlayRef) {
      this.overlayRef.dispose();
    }

    const positionStrategy = this.overlay.position()
      .flexibleConnectedTo({ x: event.clientX, y: event.clientY })
      .withFlexibleDimensions(false)
      .withPush(true)
      .withViewportMargin(10)
      .withPositions([
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetX: 20, offsetY: 20 },
        { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetX: -20, offsetY: -20 }
      ]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      // du-mouse-tooltip: click-through (styles.scss). Near the viewport edge withPush moves
      // the pane under the cursor, where it took the hover from the chart element - mouseout
      // disposed it, mouseover rebuilt it, every frame.
      panelClass: ['custom-tooltip', 'du-mouse-tooltip'],
      hasBackdrop: false
    });

    const containerPortal =
      tooltipContent instanceof TemplateRef
        ? new TemplatePortal(tooltipContent, viewContainerRef)
        : new ComponentPortal(tooltipContent, viewContainerRef);

    this.overlayRef.attach(containerPortal);

    // Just update position — NO recreation!
    this.updateTooltipPosition(tooltipContent, viewContainerRef, event);
  }

  updateTooltipPosition(
    tooltipContent: TemplateRef<any> | ComponentType<any>,
    viewContainerRef: ViewContainerRef,
    event: MouseEvent
  ) {

    if (!this.overlayRef) {
     this.createMouseTooltip(tooltipContent, viewContainerRef, event)
     return; 
    }

    const strategy = this.overlay.position()
      .flexibleConnectedTo({ x: event.clientX, y: event.clientY })
      .withFlexibleDimensions(false)
      .withPush(true)
      .withViewportMargin(10)
      .withPositions([
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetX: 20, offsetY: 20 },
        { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetX: -20, offsetY: -20 }
      ]);

    this.overlayRef.updatePositionStrategy(strategy);
  }

  hideMouseTooltip() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }
}